import { useState, useCallback, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { doRequest, prepareInternalRequest } from "shared-frontend/functions/api";

const REDIRECT_TIMEOUT_MS = 3000;

/**
 * Normalizes an error value to the `{ message, type }` shape that
 * `ErrorDisplay` (and `ErrorPropTypeShape`) expects. Plain strings, plain
 * Errors and already-shaped objects all collapse to the same structure so
 * the consent UI can render them without losing the message.
 *
 * @param {string|Object|Error|null} err The raw error.
 *
 * @returns {{ message: string, type: string }|null} The normalized error or null.
 */
const normalizeError = ( err ) => {
	if ( ! err ) {
		return null;
	}
	if ( typeof err === "string" ) {
		return { message: err, type: "error" };
	}
	if ( err.message ) {
		return { message: err.message, type: err.type || "error" };
	}
	return { message: "Something went wrong. Please try again.", type: "error" };
};

/**
 * Returns the current user's ID from state.
 *
 * @param {Object} state Application state.
 *
 * @returns {string|null} The user ID.
 */
const getUserId = ( state ) => state.user.userId;

/**
 * Hook for managing OIDC-interaction state and API calls.
 *
 * Handles fetching interaction details, establishing oidc-provider sessions,
 * resolving the interaction after a successful MyYoast login, and
 * confirming/aborting authorization requests.
 *
 * Credential-form state (submitting/error/requireOTP, the JWT decode and
 * Redux `login()` dispatch) lives on `ConsentCredentialReauth` — this hook
 * only exposes `resolveInteractionAfterLogin()` for that component to call
 * once the JWT cookie is in place.
 *
 * @param {string} interactionUid The interaction UID from the URL.
 *
 * @returns {Object} Interaction state and actions.
 */
export default function useOAuthInteraction( interactionUid ) {
	const [ interactionData, setInteractionData ] = useState( null );
	const [ loading, setLoading ] = useState( true );
	const [ error, setError ] = useState( null );
	const [ submitting, setSubmitting ] = useState( false );
	const [ redirecting, setRedirecting ] = useState( false );
	const [ redirectFailed, setRedirectFailed ] = useState( false );
	const [ redirectUrl, setRedirectUrl ] = useState( null );
	const [ redirectContext, setRedirectContext ] = useState( null );
	const [ requiresCredentialReauth, setRequiresCredentialReauth ] = useState( false );

	const redirectTimeoutRef = useRef( null );
	const userId = useSelector( getUserId );

	// Cleanup timeout on unmount
	useEffect( () => {
		return () => {
			if ( redirectTimeoutRef.current ) {
				clearTimeout( redirectTimeoutRef.current );
			}
		};
	}, [] );

	/**
	 * Initiates a redirect with timeout fallback.
	 *
	 * If the redirect doesn't work within REDIRECT_TIMEOUT_MS,
	 * sets redirectFailed to true so the UI can show a manual link.
	 *
	 * @param {string} url     The URL to redirect to.
	 * @param {string} context Description of the redirect purpose (e.g., "login", "authorize").
	 */
	const initiateRedirect = useCallback( ( url, context = "continue" ) => {
		setRedirecting( true );
		setRedirectUrl( url );
		setRedirectContext( context );
		setRedirectFailed( false );

		// Start timeout - if we're still here after timeout, redirect failed
		redirectTimeoutRef.current = setTimeout( () => {
			setRedirectFailed( true );
		}, REDIRECT_TIMEOUT_MS );

		window.location.href = url;
	}, [] );

	/**
	 * Establishes an oidc-provider session by calling the login endpoint.
	 *
	 * This bridges our JWT auth to oidc-provider's session system.
	 * The endpoint returns a redirect URL that we must navigate to,
	 * which triggers oidc-provider to create the session and redirect back.
	 *
	 * @returns {Promise<void>}
	 */
	const establishOidcSession = useCallback( async() => {
		const request = prepareInternalRequest(
			`oauth/interaction/${ interactionUid }/login`,
			"POST",
			{},
			{ credentials: "include" },
		);
		const response = await doRequest( request );

		// Navigate to the oidc-provider resume URL.
		// This triggers oidc-provider to create a session and redirects back to the consent screen.
		if ( response.redirectTo ) {
			initiateRedirect( response.redirectTo, "establish session" );
		}
	}, [ interactionUid, initiateRedirect ] );

	/**
	 * Fetches interaction details from the backend.
	 *
	 * If the oidc-provider session doesn't match the current user (or doesn't exist),
	 * triggers the login flow which will redirect away and back.
	 *
	 * @returns {Promise<void>}
	 */
	const fetchInteractionDetails = useCallback( async() => {
		if ( ! interactionUid ) {
			setError( normalizeError( "Missing interaction ID. Please try the authorization again." ) );
			setLoading( false );
			return;
		}

		try {
			// Reset submitting state in case we were in the middle of a submission
			setSubmitting( false );
			setLoading( true );
			// Include credentials to send/receive oidc-provider cookies
			const request = prepareInternalRequest(
				`oauth/interaction/${ interactionUid }`,
				"GET",
				{},
				{ credentials: "include" },
			);
			const response = await doRequest( request );

			// `login_prompt` is the OIDC `prompt=login` request reason. Per OIDC
			// Core 3.1.2.1 the AS must re-authenticate the user; we render a
			// credential form on the consent page itself rather than redirecting
			// to /login (which would loop via the silent iframe in index.jsx).
			//
			// Note: the `max_age` reason from the same Prompt has the same shape;
			// when we start honoring `max_age` explicitly, gate it here too.
			const reasons = response.prompt?.reasons ?? [];
			if ( reasons.includes( "login_prompt" ) ) {
				setInteractionData( response );
				setRequiresCredentialReauth( true );
				setError( null );
				setLoading( false );
				return;
			}

			// Otherwise, ensure the oidc-provider session matches the JWT user.
			// Needed when the prompt is "login" with a `no_session` reason, or
			// when the existing session is for a different account.
			const needsLogin = response.prompt?.name === "login" || response.sessionAccountId !== userId;

			if ( needsLogin && userId ) {
				// This will redirect away and back via window.location.href.
				// Keep loading state to avoid flash of error screen.
				await establishOidcSession();
				return;
			}

			setInteractionData( response );
			setError( null );
			setLoading( false );
		} catch ( err ) {
			console.error( "Failed to fetch interaction details:", err );
			setError( normalizeError( "This authorization request has expired or is invalid. Please try again." ) );
			setLoading( false );
		}
		// We're deliberately not using finally here. We want to keep loading=true if we redirect for login.
	}, [ interactionUid, userId, establishOidcSession ] );

	/**
	 * Resolves the OIDC interaction after the JWT cookie has been set by an
	 * upstream login step (e.g. `ConsentCredentialReauth` posting to
	 * `/Customers/login-user/`).
	 *
	 * Posts to `/oauth/interaction/:uid/login`, which oidc-provider's
	 * `interactionResult` turns into a resume URL we follow.
	 *
	 * Owns the error surface for resolver failures: sets the hook's `error`
	 * so the consent screen renders it above the credential form, and
	 * deliberately does NOT rethrow. If both the hook and the credential
	 * component reacted to the same failure, the user would see the same
	 * message twice in different surfaces.
	 *
	 * @returns {Promise<boolean>} `true` when a redirect was initiated (caller
	 *   should leave its submit state locked because navigation is imminent),
	 *   `false` when the resolver failed (caller should unlock so the user can
	 *   see the hook-level error and retry).
	 */
	const resolveInteractionAfterLogin = useCallback( async() => {
		setError( null );

		try {
			const request = prepareInternalRequest(
				`oauth/interaction/${ interactionUid }/login`,
				"POST",
				{},
				{ credentials: "include" },
			);
			const response = await doRequest( request );

			if ( ! response?.redirectTo ) {
				// The resolve endpoint should always return a redirectTo URL
				// (oidc-provider's `interactionResult` produces one). If it
				// doesn't, fail loudly here so the user isn't stuck on a
				// stale credential form with no error visible.
				throw new Error( "Login could not be completed. Please try again." );
			}
			initiateRedirect( response.redirectTo, "complete login" );
			return true;
		} catch ( err ) {
			setError( normalizeError( err ) || normalizeError( "Login could not be completed. Please try again." ) );
			return false;
		}
	}, [ interactionUid, initiateRedirect ] );

	/**
	 * Confirms user consent for the OAuth authorization.
	 *
	 * @returns {Promise<void>} Resolves when consent is confirmed.
	 */
	const authorize = useCallback( async() => {
		setSubmitting( true );
		setError( null );

		try {
			// Include credentials to send oidc-provider session cookie
			const request = prepareInternalRequest(
				`oauth/interaction/${ interactionUid }/confirm`,
				"POST",
				{},
				{ credentials: "include" },
			);
			const response = await doRequest( request );

			// Redirect to the URL provided by the backend
			if ( response.redirectTo ) {
				initiateRedirect( response.redirectTo, "complete authorization" );
			}
		} catch ( err ) {
			console.error( "Failed to confirm consent:", err );
			setError( normalizeError( "Failed to process your authorization. Please try again." ) );
			setSubmitting( false );
		}
	}, [ interactionUid, initiateRedirect ] );

	/**
	 * Aborts the OAuth authorization request.
	 *
	 * @returns {Promise<void>} Resolves when authorization is aborted.
	 */
	const cancel = useCallback( async() => {
		setSubmitting( true );
		setError( null );

		try {
			// Include credentials to send oidc-provider session cookie
			const request = prepareInternalRequest(
				`oauth/interaction/${ interactionUid }/abort`,
				"POST",
				{},
				{ credentials: "include" },
			);
			const response = await doRequest( request );

			// Redirect to the URL provided by the backend (with error)
			if ( response.redirectTo ) {
				initiateRedirect( response.redirectTo, "cancel authorization" );
			}
		} catch ( err ) {
			console.error( "Failed to abort authorization:", err );
			setError( normalizeError( "Failed to process your request. Please try again." ) );
			setSubmitting( false );
		}
	}, [ interactionUid, initiateRedirect ] );

	return {
		interactionData,
		loading,
		error,
		submitting,
		redirecting,
		redirectFailed,
		redirectUrl,
		redirectContext,
		requiresCredentialReauth,
		fetchInteractionDetails,
		authorize,
		cancel,
		resolveInteractionAfterLogin,
	};
}
